<template>
  <div class="markers-wrapper">
    <div ref="canvasWrapper" :style="bgStyle">
      <PolygonControls v-if="activeElementType === 'tag'" :scale="scale" />

      <div v-for="tag in visibleTags" :key="tag._id">
        <div v-for="polygon in tag.polygons" :key="polygon._id">
          <polygon-shape
            :id="polygon._id"
            :title="tagPolygonTitle(tag)"
            :polygon="polygon"
            :scale="scale"
            :isTagSelected="isParentSelected(polygon._id)"
            :isSelected="activeElement === polygon"
            @updatePolygon="polygon = $event"
            @clearSelection="clearSelection"
            @record-before-action="$emit('record-before-action', $event)"
            @record-action="$emit('record-action', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import PolygonShape from "@/components/PolygonShape";
import PolygonControls from "@/components/PolygonControls";

export default {
  name: "TagPolygons",

  props: [
    "tags",
    "active-element",
    "active-element-type",
    "active-slide",
    "zoom-level",
  ],

  components: {
    PolygonShape,
    PolygonControls,
  },

  data() {
    return {
      scale: 1,
    };
  },

  computed: {
    ...mapGetters({
      activeLanguage: "activeLanguage",
    }),

    ...mapState({
      editPolygonId: (state) => state.draggable.editPolygonId,
    }),

    visibleTags() {
      const tags =
        this.activeSlide && Array.isArray(this.activeSlide.tags)
          ? this.activeSlide.tags
          : this.tags;

      if (!Array.isArray(tags)) return [];
      return tags;
    },

    bgStyle() {
      if (this.scale) {
        return {
          ["font-size"]: 20 * this.scale + "px",
          ["height"]: 1960 * this.scale + "px",
        };
      }

      return {};
    },
  },

  watch: {
    zoomLevel() {
      this.getScaledAmount();
    },
  },

  mounted() {
    window.addEventListener("resize", this.getScaledAmount);
    this.$nextTick(() => {
      this.getScaledAmount();
    });
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.getScaledAmount);
  },

  methods: {
    defaultPolygon() {
      return {
        fill: "rgba(0,0,0,0)",
        stroke: "rgba(255,255,255,0)",
        strokeWidth: 8,
        points: [],
        dropShadow: false,
      };
    },

    ensurePolygon(tag) {
      if (!tag) return this.defaultPolygon();

      if (!tag.polygon || typeof tag.polygon !== "object") {
        this.$set(tag, "polygon", this.defaultPolygon());
      }

      const defaults = this.defaultPolygon();
      Object.keys(defaults).forEach((key) => {
        if (typeof tag.polygon[key] === "undefined") {
          this.$set(tag.polygon, key, defaults[key]);
        }
      });

      if (!Array.isArray(tag.polygon.points)) {
        this.$set(tag.polygon, "points", []);
      }

      return tag.polygon;
    },

    getScaledAmount() {
      if (this.$refs.canvasWrapper) {
        const canvasWidth =
          this.$refs.canvasWrapper.getBoundingClientRect().width;
        this.scale = canvasWidth / 3190;
      }
    },

    isTagSelected(tag) {
      return (
        this.activeElementType === "tag" &&
        this.activeElement &&
        this.activeElement._id === tag._id
      );
    },

    isParentSelected(polygonId) {
      if (this.activeElementType === "polygon") return false;
      if (!this.activeElement) return false;

      return this.activeElement.polygons.some(
        (polygon) => polygon._id === polygonId
      );
    },

    isPolygonSelected(tag) {
      if (!tag) return false;

      if (
        this.activeElementType === "polygon" &&
        this.activeElement &&
        this.activeElement._id === tag._id
      ) {
        return true;
      }

      return this.editPolygonId === tag._id;
    },

    tagPolygonTitle(tag) {
      if (!tag) return "";

      const langCode =
        this.activeLanguage && this.activeLanguage.code
          ? this.activeLanguage.code
          : "en";

      if (tag.label) {
        if (typeof tag.label === "string") return tag.label;
        return tag.label[langCode] || tag.label.en || tag._id;
      }

      if (tag.name) return tag.name;
      if (tag.title) return tag.title;

      return tag._id;
    },

    updatePolygon(tag, polygon) {
      this.ensurePolygon(tag);
      tag.polygon = polygon;
    },

    clearSelection() {
      this.$store.commit("draggable/deselectAll");
      this.$emit("selectedElement", null, "");
    },
  },
};
</script>

<style lang="scss" scoped>
.markers-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}
</style>
